import { i18n } from "@lingui/core";

/**
 * 8 mandatory locales (PontiMed contract): en, ro, bg, de, fr, hu, it, el.
 * 7 secondary (English fallback until translated): hr, sr, cs, es, pl, tr, uk.
 *
 * All locales lazy-load on demand — including en + ro. Eager-importing the
 * canonical pair previously added ~300 KB to the main bundle. Boot now
 * dynamic-imports only the detected locale before mounting React.
 * Cookie scope `.pontimed.app` — locale choice persists across portal/admin/web.
 */
export const SUPPORTED_LOCALES = [
  "en",
  "ro",
  "bg",
  "de",
  "fr",
  "hu",
  "it",
  "el",
  "hr",
  "sr",
  "cs",
  "es",
  "pl",
  "tr",
  "uk",
] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export const PRIMARY_LOCALES: SupportedLocale[] = ["en", "ro", "bg", "de", "fr", "hu", "it", "el"];

export const DEFAULT_LOCALE: SupportedLocale = "en";

const LOCALE_COOKIE = "pontimed_locale";
const LOCALE_STORAGE_KEY = "pontimed.locale";

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name: string, value: string, days = 365): void {
  if (typeof document === "undefined") return;
  const expires = new Date(Date.now() + days * 86400_000).toUTCString();
  const isHttps = location.protocol === "https:";
  const isPontimedHost = location.hostname.endsWith("pontimed.app");
  const domain = isPontimedHost ? "; Domain=.pontimed.app" : "";
  const secure = isHttps ? "; Secure" : "";
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Expires=${expires}; SameSite=Lax${secure}${domain}`;
}

export function isSupportedLocale(value: string): value is SupportedLocale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

export function detectInitialLocale(): SupportedLocale {
  if (typeof window === "undefined") return DEFAULT_LOCALE;

  const fromCookie = readCookie(LOCALE_COOKIE);
  if (fromCookie && isSupportedLocale(fromCookie)) return fromCookie;

  const fromStorage = window.localStorage?.getItem(LOCALE_STORAGE_KEY);
  if (fromStorage && isSupportedLocale(fromStorage)) return fromStorage;

  for (const candidate of navigator.languages ?? [navigator.language]) {
    const short = candidate.toLowerCase().split("-")[0];
    if (isSupportedLocale(short)) return short;
  }

  return DEFAULT_LOCALE;
}

const loadedLocales = new Set<SupportedLocale>();

export async function loadLocale(locale: SupportedLocale): Promise<void> {
  if (loadedLocales.has(locale)) return;
  const mod = await import(`@/locales/${locale}.ts`);
  i18n.load(locale, mod.messages);
  loadedLocales.add(locale);
}

export async function setLocale(locale: SupportedLocale): Promise<void> {
  await loadLocale(locale);
  i18n.activate(locale);
  writeCookie(LOCALE_COOKIE, locale);
  if (typeof window !== "undefined") {
    window.localStorage?.setItem(LOCALE_STORAGE_KEY, locale);
    document.documentElement.lang = locale;
  }
}

/** Boot-time hydration. Call once in main.tsx before <RouterProvider />. */
export async function hydrateInitialLocale(): Promise<SupportedLocale> {
  const detected = detectInitialLocale();
  await setLocale(detected);
  return detected;
}

export { i18n };
